/* eslint-disable sonarjs/cognitive-complexity */
import { errorToast } from '@/sagas/commonGenerators/errorToast';
import { ERROR_TOAST_DURATION } from '@/services/constants';
import { addList, loadSingleContact } from '@/services/ElasticApi';
import { getDialogStore, HideDialog, UpdateDialog } from '@/store/dialog/actions';
import { ListsGetNamesStart } from '@/store/lists/actions';
import { getMediaManagerStore, HideMediaManager } from '@/store/mediaManager/actions';
import {
    AccountCore,
    ContactStatus,
    EncodingType,
    Form,
    FormPageTypeEnum,
    FormPayload,
    FormSettingsEnum,
    LandingPage,
    LandingPagePayload,
    Template,
    TemplateScope,
    TemplateType,
} from '@dev/ee-api/ee-api-models';
import { sendEventToGTM } from '@dev/ee-tools';
import {
    EditorSaveData,
    EditorSignUpField,
    EditorTemplateVersionBlocks,
    editorWebsiteBlocksMap,
} from '@dev/template-editor';
import { filter, find, forEach, includes, isEmpty, isNil, remove } from 'lodash-es';
import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import isEmail from 'validator/lib/isEmail';

import { getRecaptchaScript } from '@/services/tools/getRecaptchaScript';
import { deleteArrayForms, formsPagePreview, loadForm, saveForm } from '@/services/ApiCategories/forms';
import { generateLandingPageMetaTags } from '@/services/tools/generateLandingPageMetaTags';
import { checkIsUrlInvalid } from '@/services/tools/urlValidation/checkIsUrlInvalid';
import {
    AddContactListErrorStatus,
    CloseGallery,
    getLandingPageCreatorStore,
    LoadError,
    LoadPageTemplateSuccess,
    LoadSuccess,
    // SaveAllFormsError,
    // SaveAllFormsSuccess,
    SaveError,
    SaveSuccess,
    SetDefaultTemplate,
    SetIsTemplateOpen,
    SetLandingPageErrors,
    SetTemplatePreview,
    TemplateFormAddLoaded,
    TemplateFormAddNew,
    TemplateFormPayloadUpdate,
    TemplateFormSetErrors,
    TemplateFormsFromReplacedTemplateClear,
    TemplateFormsRemove,
    TemplateFormsSetInvalid,
    TemplateFormUpdateSaved,
    TemplateLoadError,
    TemplateLoadSingleError,
    TemplateLoadSingleSuccess,
    TemplateLoadStart,
    TemplateLoadSuccess,
} from '@/store/landingPageCreator/actions';
import {
    AddContactListStartAction,
    EditorMetaData,
    EditorTemplateForm,
    LandingPageCreatorActionTypes,
    LandingPageCreatorState,
    LandingPageCreatorTemplate,
    LandingPageErrorNames,
    LandingPageMetaData,
    LoadPageTemplateStartAction,
    LoadStartAction,
    SaveStartAction,
    TemplateFormErrorNames,
    TemplateFormsCheckInvalidAction,
    TemplateLoadSingleStartAction,
    TemplateLoadStartAction,
    // TemplateLoadSuccessAction,
    TemplateOpenFromGalleryAction,
    TemplatePreviewFromGalleryAction,
    TemplateSaveSettings,
} from '@/store/landingPageCreator/types';
import { LoadLandingPageSubdomain } from '@/store/landingPageSubdomain/actions';
import { getMultiSignUpPublicFormIdDefaultValue } from '@/components/LandingPageCreator/hooks/useMultiSignUpSettings';
import { getShowMessagesScript } from '@/services/tools/getShowMessagesScript';
import { Logger } from '@/services/errors/appLogger';
import i18n from '../../i18n';
import { loadLanding as fetchLanding, saveLanding as updateLanding } from '../services/ApiCategories/landings';
import {
    saveTemplate as createTemplate,
    loadTemplate as fetchTemplate,
    getDefaultTemplateObj,
    previewTemplate,
    updateTemplate,
} from '../services/ApiCategories/templates';
import { HideToast, ShowToast } from '../store/toast/actions';

const ShowTemplateErrorToast = (message: string, width = 485) =>
    ShowToast({
        severity: 'error',
        message,
        duration: ERROR_TOAST_DURATION,
        width,
        onClose: HideToast,
    });

const ShowTemplateInfoToast = (message: string, width = 485) =>
    ShowToast({
        severity: 'info',
        message,
        duration: ERROR_TOAST_DURATION,
        width,
        onClose: HideToast,
    });

function* onClose(): any {
    const { isOpened } = yield select(getDialogStore);
    const { isOpened: isMediaManagerOpened } = yield select(getMediaManagerStore);
    if (isOpened) {
        yield put(HideDialog());
    }
    if (isMediaManagerOpened) {
        yield put(HideMediaManager());
    }
}

function* load(action: LoadStartAction): any {
    const { payload } = action;
    const { name } = payload;

    try {
        const isPublic = false;
        const landing: LandingPage = yield call(fetchLanding, name, isPublic);
        const template: Template = yield call(fetchTemplate, name, isPublic, TemplateType.LandingPageTemplate);

        const metaData: EditorMetaData | null = template?.EditorMetaData
            ? JSON.parse(Buffer.from(template.EditorMetaData, 'base64').toString('utf8'))
            : null;
        yield all(
            (metaData?.data?.forms ?? [])?.map(({ blockClientId, formName, formMailingPreferencesLabel }) =>
                call(loadTemplateForm, blockClientId, formName, formMailingPreferencesLabel)
            )
        );

        //we need to always load subdomain for the landing to get proper validity of steps
        yield put(LoadLandingPageSubdomain(landing?.LandingPageID));

        yield put(LoadSuccess(landing, template, landing?.LandingMetaJson));
    } catch (e) {
        yield put(LoadError());
        yield call(errorToast, e);
    }
}

interface FormMultiBlockData {
    blockClientId: string;
    fieldNames?: string[];
    isRecaptchaRequired: boolean;
}

export const findFormsMulti = (blocks: EditorTemplateVersionBlocks): FormMultiBlockData[] => {
    let forms: FormMultiBlockData[] = [];
    blocks?.forEach((block) => {
        if (block.innerBlocks) {
            forms = [...forms, ...findFormsMulti(block.innerBlocks)];
        }
        if (editorWebsiteBlocksMap[block.name] !== 'formMulti') {
            return;
        }
        forms.push({
            blockClientId: block.clientId,
            fieldNames: (block.attributes?.fields as EditorSignUpField[])?.map(({ name }) => name),
            isRecaptchaRequired: !!block.attributes?.isRecaptchaRequired,
        });
    });
    return forms;
};

function* checkTemplateFormsByAction(action: TemplateFormsCheckInvalidAction): any {
    yield call(checkTemplateForms, action.payload.blockClientIds);
}

function* getContactStatus(email: string) {
    try {
        const contact = yield call(loadSingleContact, email);
        return contact.Status;
    } catch (e) {
        return 'Error';
    }
}

function* verifyEmailsToNotify(templateForm: EditorTemplateForm) {
    const errors: Partial<Record<TemplateFormErrorNames, boolean>> = {};
    const emailsToNotify = templateForm?.formPayload?.EmailsToNotifyOnAction?.split(';').map((item) => item.trim());
    const emailsNumberFromString = (templateForm?.formPayload?.EmailsToNotifyOnAction?.split('@').length ?? 0) - 1;

    if (!isEmpty(emailsToNotify)) {
        const emailsInvalid = !!find(emailsToNotify, (email) => !isEmail(email));
        if (emailsInvalid) {
            errors[TemplateFormErrorNames.NOTIFY_INVALID_EMAILS] = true;
        }
        if (emailsNumberFromString > 1 && emailsNumberFromString !== emailsToNotify.length) {
            errors[TemplateFormErrorNames.NOTIFY_EMAILS_NO_PROPER_SEPARATOR] = true;
        }
        if (!emailsInvalid && emailsToNotify.length > 5) {
            errors[TemplateFormErrorNames.NOTIFY_EMAILS_NUMBER_EXCEEDED] = true;
        }
        const recipientsStatuses = yield all(emailsToNotify.map((email) => call(getContactStatus, email)));
        const emailsStatusInvalid = !!find(
            recipientsStatuses,
            (status) => status !== ContactStatus.Active && status !== ContactStatus.Engaged
        );
        if (!emailsInvalid && !!emailsStatusInvalid) {
            errors[TemplateFormErrorNames.NOTIFY_EMAILS_NOT_ON_CONTACT_LIST] = true;
        }
    }

    if (!isEmpty(errors)) {
        yield put(TemplateFormSetErrors(errors, templateForm?.blockClientId));
    }

    return { errors };
}

function* verifyFormSubscriptionSettings(templateForm: EditorTemplateForm) {
    const errors: Partial<Record<TemplateFormErrorNames, boolean>> = {};
    const { blockClientId, formPayload } = templateForm;

    if (
        formPayload?.FormPageType?.[1] === FormPageTypeEnum.ExternalWebsiteFormSubmitPage &&
        (!formPayload?.FormSubmitPage || checkIsUrlInvalid(formPayload?.FormSubmitPage))
    ) {
        errors[TemplateFormErrorNames.FORM_SUBMIT_INVALID_URL] = true;
    }

    if (
        formPayload?.FormPageType?.[2] === FormPageTypeEnum.ExternalWebsiteFormSubscribedPage &&
        (!formPayload?.FormAlreadySubscribedPage || checkIsUrlInvalid(formPayload?.FormAlreadySubscribedPage))
    ) {
        errors[TemplateFormErrorNames.ALREADY_SUBSCRIBED_INVALID_URL] = true;
    }

    if (formPayload?.FormPageType?.[1] === FormPageTypeEnum.LandingPageFormSubmitPage && !formPayload?.FormSubmitPage) {
        errors[TemplateFormErrorNames.FORM_SUBMIT_NONE_LP] = true;
    }
    if (
        formPayload?.FormPageType?.[2] === FormPageTypeEnum.LandingPageFormSubscribedPage &&
        !formPayload?.FormAlreadySubscribedPage
    ) {
        errors[TemplateFormErrorNames.ALREADY_SUBSCRIBED_NONE_LP] = true;
    }

    yield put(TemplateFormSetErrors(errors, blockClientId));

    return { errors };
}

function* isTemplateFormInvalid(templateForm: EditorTemplateForm): any {
    const { formPayload, form } = templateForm;
    if (!templateForm || templateForm.isDeleted === true) {
        return true;
    }
    if (!templateForm?.formPayload?.Audience?.length) {
        // AT LEAST ONE CONTACT LIST IS REQUIRED
        return true;
    }

    const checkFormSettingsFieldsExists = (formPayload: FormPayload, formObject: Form): boolean => {
        const formAlreadySubscribedPage =
            formPayload?.FormAlreadySubscribedPage ?? formObject?.FormAlreadySubscribedPage;
        const formSubmitPage = formPayload?.FormSubmitPage ?? formObject?.FormSubmitPage;

        return typeof formAlreadySubscribedPage !== 'undefined' && typeof formSubmitPage !== 'undefined';
    };

    //subscription section validation
    if (checkFormSettingsFieldsExists(formPayload, form)) {
        const { errors } = yield call(verifyFormSubscriptionSettings, templateForm);
        if (!isEmpty(errors)) return true;
    }

    // Notifications section validation
    if (includes(templateForm?.formPayload?.FormSettings, FormSettingsEnum.NotifyEmails)) {
        const { errors } = yield call(verifyEmailsToNotify, templateForm);
        if (!isEmpty(errors)) return true;
    }
}

function* getFormValidity(forms: EditorTemplateForm[], clientId: string) {
    const matchedTemplateForm = forms?.find((templateForm) => templateForm.blockClientId === clientId);
    const isInvalid = yield call(isTemplateFormInvalid, matchedTemplateForm);
    if (!isInvalid) return;
    return clientId;
}

function* checkTemplateForms(formsMultiIds?: string[], suppressErrors?: boolean): any {
    const {
        template,
    }: {
        template: Partial<LandingPageCreatorTemplate>;
    } = yield select(getLandingPageCreatorStore);
    const formsMulti =
        formsMultiIds ?? template.forms?.filter((form) => !form.isDeleted).map((form) => form.blockClientId);

    const formsValidity = yield all(formsMulti.map((clientId) => call(getFormValidity, template.forms, clientId)));
    const invalidForms = filter(formsValidity);

    const isSaveBlocked = !!invalidForms.length && !suppressErrors;
    yield put(TemplateFormsSetInvalid(isSaveBlocked ? invalidForms : []));
    return isSaveBlocked;
}

const getFormSettingsWithRecaptcha = (
    isRecaptchaRequired: boolean,
    formSettings: FormSettingsEnum[]
): FormSettingsEnum[] => {
    if (isRecaptchaRequired && !formSettings.length) {
        return [FormSettingsEnum.RequireReCaptcha];
    }
    const isRecaptchaInSettings = includes(formSettings, FormSettingsEnum.RequireReCaptcha);
    if (isRecaptchaRequired && !isRecaptchaInSettings) {
        return [...formSettings, FormSettingsEnum.RequireReCaptcha];
    }
    if (!isRecaptchaRequired && isRecaptchaInSettings) {
        return remove([...formSettings], (setting) => setting !== FormSettingsEnum.RequireReCaptcha);
    }
    return [...formSettings];
};

interface SavedForm {
    blockClientId: string;
    publicformid: string;
    isNew: boolean;
}

function* saveTemplateForms(data?: Partial<EditorSaveData>, exitOnSave?: boolean, suppressErrors?: boolean): any {
    if (!data) {
        return { isSaveBlocked: false };
    }
    const {
        template,
    }: {
        template: Partial<LandingPageCreatorTemplate>;
    } = yield select(getLandingPageCreatorStore);
    const alreadyHadError = !!template.forms?.find((form) => !form.isDeleted && form.isError);

    const formsMulti = findFormsMulti(data?.contentJson?.blocks);

    const formsToCreate = formsMulti.filter(({ blockClientId }) => {
        const matchedTemplateForm = template.forms?.find(
            (templateForm) => templateForm.blockClientId === blockClientId
        );
        return !matchedTemplateForm || matchedTemplateForm.isDeleted === true;
    });
    const formsToRemove: string[] = [];
    const formsToRemoveBlockClientIds: string[] = [];
    forEach(template?.forms ?? [], (existingForm) => {
        if (
            !existingForm.isDeleted &&
            !formsMulti.find(({ blockClientId }) => blockClientId === existingForm.blockClientId)
        ) {
            if (existingForm.form) {
                formsToRemove.push(existingForm.form.Name);
            }
            formsToRemoveBlockClientIds.push(existingForm.blockClientId);
        }
    });

    if (template.formsFromReplacedTemplate?.length) {
        yield call(
            deleteArrayForms,
            template.formsFromReplacedTemplate.map((templateForm) => templateForm.form.Name)
        );
        yield put(TemplateFormsFromReplacedTemplateClear());
    }

    if (formsToRemove.length) {
        yield call(deleteArrayForms, formsToRemove);
        yield put(TemplateFormsRemove(formsToRemoveBlockClientIds));
    }

    yield all(formsToCreate.map(({ blockClientId }) => put(TemplateFormAddNew(blockClientId))));
    const isSaveBlocked = yield call(
        checkTemplateForms,
        formsMulti.map(({ blockClientId }) => blockClientId),
        suppressErrors
    );
    yield all(
        formsMulti.map(({ blockClientId, fieldNames, isRecaptchaRequired }) => {
            const templateForm = template?.forms?.find(
                ({ blockClientId: templateFormBlockClientId }) => blockClientId === templateFormBlockClientId
            );
            const formSettings = templateForm?.formPayload?.FormSettings ?? templateForm?.form?.FormSettings ?? [];
            const newFormSettings = getFormSettingsWithRecaptcha(isRecaptchaRequired, formSettings);
            return put(
                TemplateFormPayloadUpdate(blockClientId, {
                    MergeFields: fieldNames?.join(','),
                    FormSettings: newFormSettings,
                })
            );
        })
    );

    const {
        template: updatedTemplates,
    }: {
        template: Partial<LandingPageCreatorTemplate>;
    } = yield select(getLandingPageCreatorStore);

    if (isSaveBlocked && !suppressErrors) {
        if (exitOnSave) {
            yield put(ShowTemplateErrorToast(i18n.t('fixerrorstocontinue'), 336));
        } else if (!alreadyHadError) {
            yield put(ShowTemplateInfoToast(i18n.t('fixerrorstoenableautosave'), 386));
        }
        return { isSaveBlocked: true };
    }
    let savedFormsPublicIds;
    if (updatedTemplates.forms) {
        savedFormsPublicIds = yield all(
            filterUndeletedEditorTemplateForm(updatedTemplates.forms).map((item) =>
                call(saveTemplateForm, item.blockClientId, item.formPayload, item?.form?.Name)
            )
        );
    }
    savedFormsPublicIds = savedFormsPublicIds.filter((savedForm) => savedForm.isNew); //FILTER SAVED TO CREATED ONLY
    return { isSaveBlocked: false, savedFormsPublicIds };
}

function* saveTemplateForm(blockClientId: string, formPayload: FormPayload, name?: string): any {
    const response = yield call(saveForm, formPayload, name);
    yield put(TemplateFormUpdateSaved(blockClientId, response));
    return { blockClientId, publicformid: response.PublicFormID, isNew: !name };
}

function* loadTemplateForm(blockClientId: string, name: string, formMailingPreferencesLabel?: string): any {
    const response = yield call(loadForm, name);
    yield put(TemplateFormAddLoaded(blockClientId, response, formMailingPreferencesLabel));
}

const filterUndeletedEditorTemplateForm = (forms?: EditorTemplateForm[]): EditorTemplateForm[] =>
    forms?.filter((form) => !form.isDeleted) ?? [];

function* getTemplateToSave(name: string, templateData?: EditorSaveData, savedForms?: SavedForm[]): any {
    const {
        templateObject,
        template,
        templateSaveSettings,
    }: {
        templateObject: Partial<Template>;
        template: Partial<LandingPageCreatorTemplate>;
        templateSaveSettings: TemplateSaveSettings;
    } = yield select(getLandingPageCreatorStore);

    const metaDataPayload: EditorMetaData = {
        data: {
            contentJson: templateData?.contentJson,
            recentColors: template.recentColors ?? [],
            forms: filterUndeletedEditorTemplateForm(template.forms).map((form) => ({
                blockClientId: form.blockClientId,
                formName: form.formPayload.Name,
                formMailingPreferencesLabel: form.formMailingPreferencesLabel,
            })),
        },
    };

    let content = templateData?.content;
    if (content && savedForms?.length) {
        savedForms.forEach((savedForm) => {
            content = content.replace(
                getMultiSignUpPublicFormIdDefaultValue(savedForm.blockClientId),
                savedForm.publicformid
            );
        });
    }
    const screenshot = templateSaveSettings?.customScreenshot || templateData?.screenshot;

    return {
        TemplateType: TemplateType.LandingPageTemplate,
        ...templateObject,
        Name: name,
        ...(screenshot ? { Base64Image: screenshot } : null),
        EditorMetaData: templateData?.contentJson
            ? Buffer.from(JSON.stringify(metaDataPayload)).toString('base64')
            : templateObject?.EditorMetaData,
        TemplateScope:
            templateSaveSettings?.isPublic ||
            (isNil(templateSaveSettings?.isPublic) && templateObject?.TemplateScope === TemplateScope.Public)
                ? TemplateScope.Public
                : TemplateScope.Private,
        IsPublic: false,
        IsDefault: false,
        Body: {
            Parts: [
                ...(content
                    ? [
                          {
                              Charset: null,
                              Content: content,
                              ContentType: 'text/html',
                              Encoding: EncodingType.None,
                          },
                      ]
                    : []),
            ],
        },
    };
}

const getLandingPageMetadata = (
    landingPageMetaDataPayload: LandingPageMetaData,
    landingPageMetaDataObject: LandingPageMetaData
): LandingPageMetaData => {
    if (!landingPageMetaDataPayload && !landingPageMetaDataObject) {
        // it should not be saved before
        return null;
    }
    const payload: LandingPageMetaData = {
        data: {
            socialPreview: {
                imageUrl: landingPageMetaDataObject?.data?.socialPreview?.imageUrl ?? '',
                pageTitle: landingPageMetaDataObject?.data?.socialPreview?.pageTitle ?? '',
                pageDescription: landingPageMetaDataObject?.data?.socialPreview?.pageDescription ?? '',
                ...landingPageMetaDataPayload?.data?.socialPreview,
            },
        },
    };
    return payload;
};

// function* saveFormAfterTemplateisLoaded({ payload }: TemplateLoadSuccessAction): any {
//     const {
//         template,
//     }: {
//         template: Partial<LandingPageCreatorTemplate>;
//     } = yield select(getLandingPageCreatorStore);

//     try {
//         const formsMulti = findFormsMulti(template?.loadedJson?.blocks);
//         const formsWithoutPublicFormIdOrFromNotThisTemplate =
//             !template?.forms?.length ||
//             template.forms?.some(
//                 (templateForm) =>
//                     !templateForm?.form?.PublicFormID ||
//                     formsMulti.find((form) => form.blockClientId !== templateForm.blockClientId)
//             );

//         //we want to only save forms when going from gallery, and if there is any form without public  form id withing template
//         if (
//             !payload?.isPublic ||
//             !formsMulti?.length ||
//             (!!formsMulti?.length && !formsWithoutPublicFormIdOrFromNotThisTemplate)
//         ) {
//             yield put(SaveAllFormsSuccess());
//             return;
//         }

//         //sae only forms
//         yield call(saveTemplateForms, { contentJson: template.loadedJson }, true, true);

//         yield put(SaveAllFormsSuccess());
//     } catch (e) {
//         yield call(errorToast, e);
//         yield put(SaveAllFormsError());
//     }
// }

function* save({ payload, meta }: SaveStartAction): any {
    const { data: templateData, exitOnSave, errors } = payload || {};
    const {
        templateSaveSettings,
    }: {
        templateSaveSettings: TemplateSaveSettings;
    } = yield select(getLandingPageCreatorStore);

    //SAVE FORMS BEFORE LANDING PAGE
    const { isSaveBlocked, savedFormsPublicIds } = yield call(
        saveTemplateForms,
        !templateSaveSettings?.isCreator && templateData,
        exitOnSave
    ); // do nott save forms as creator

    if (isSaveBlocked) {
        yield put(SaveError(meta));
        return;
    }

    try {
        if (errors) {
            throw errors;
        }

        const {
            landingPageObject,
            landingPagePayload,
            templateObject,
            template,
            landingPageMetaDataPayload,
            landingPageMetaDataObject,
        }: {
            landingPageObject: Partial<LandingPage>;
            landingPagePayload: Partial<LandingPage>;
            templateObject: Partial<Template>;
            template: Partial<LandingPageCreatorTemplate>;
            landingPageMetaDataObject: LandingPageMetaData;
            landingPageMetaDataPayload: LandingPageMetaData;
        } = yield select(getLandingPageCreatorStore);

        const formsUsed = filterUndeletedEditorTemplateForm(template?.forms);
        const isRecaptchaUsed = formsUsed?.find((templateForm) =>
            templateForm.form?.FormSettings?.includes(FormSettingsEnum.RequireReCaptcha)
        );
        const currentLandingPageName = landingPageObject?.Name ?? landingPagePayload?.Name;
        const newLandingPageName = landingPagePayload?.Name ?? landingPageObject?.Name;
        const landingPageMetaData =
            landingPageMetaDataPayload || landingPageMetaDataObject
                ? getLandingPageMetadata(landingPageMetaDataPayload, landingPageMetaDataObject)
                : null;
        const landingToSave: LandingPagePayload = {
            Name: newLandingPageName,
            LandingTemplateName: newLandingPageName,
            FormNames: formsUsed?.map((item) => item.form.Name).join(','),
            LandingMetaJson: landingPageMetaData
                ? Buffer.from(JSON.stringify(landingPageMetaData)).toString('base64')
                : null,
            LandingMetaHtml: `${landingPageMetaData ? generateLandingPageMetaTags(landingPageMetaData) : ''}${
                isRecaptchaUsed ? getRecaptchaScript() : ''
            }${getShowMessagesScript()}`,
        };

        const templateToSave: Template = yield call(
            getTemplateToSave,
            newLandingPageName,
            templateData,
            savedFormsPublicIds
        );

        let savedTemplate;
        if (!templateObject) {
            savedTemplate = yield call(createTemplate, templateToSave);
        } else {
            savedTemplate = yield call(updateTemplate, templateObject.Name, templateToSave);
        }
        const savedLandingPage = yield call(
            updateLanding,
            landingToSave,
            landingPageObject ? currentLandingPageName : null
        ); //SAVE LP AFTER TEMPLATE TO ENSURE NO NAME COLLISION

        yield put(SaveSuccess(savedLandingPage, savedTemplate, templateData, landingPageMetaData, meta));
        if (exitOnSave) {
            yield put(SetIsTemplateOpen(false));
        }

        const isFirstTotalLandingPageSave =
            isNil(landingPageMetaDataObject?.data?.socialPreview) &&
            !isNil(landingPageMetaDataPayload?.data?.socialPreview);
        if (isFirstTotalLandingPageSave) {
            const generalInfo = yield select((state): AccountCore => state?.account?.GeneralInfo);
            sendEventToGTM('landing page generated', {
                productType: generalInfo?.ProductType,
            });
        }
    } catch (e) {
        if (e?.response?.data?.MessageID === 'template_name_in_use') {
            yield put(SetLandingPageErrors({ [LandingPageErrorNames.DUPLICATED_NAME]: true }));
            return;
        }
        yield put(SaveError());
        yield call(errorToast, e);
    }
}

function* loadTemplateSingle({ payload, meta }: TemplateLoadSingleStartAction): any {
    const { templateName, isPublic } = payload;
    if (!templateName) {
        yield put(TemplateLoadSingleError(meta));
        return;
    }
    const template: Template = yield call(fetchTemplate, templateName, isPublic);
    if (!template || !template?.Screenshot) {
        yield put(TemplateLoadSingleError(meta));
        return;
    }
    yield put(TemplateLoadSingleSuccess(template, meta));
}

function* loadTemplate(action: TemplateLoadStartAction): any {
    const { payload } = action;
    const { name, isPublic, useDraftScope } = payload;

    try {
        if (!name) {
            const getTemplate = yield call(getDefaultTemplateObj, TemplateType.LandingPageTemplate);
            yield put(SetDefaultTemplate({ templateObject: getTemplate?.data }));
        } else {
            const template: Template = yield call(fetchTemplate, name, isPublic, TemplateType.LandingPageTemplate);
            yield put(TemplateLoadSuccess(template, isPublic, useDraftScope));
        }
    } catch (e) {
        yield put(TemplateLoadError());
        yield call(errorToast, e);
    }
}

function* openTemplateFromGallery(action: TemplateOpenFromGalleryAction): any {
    const { templateName, isPublic } = action.payload;
    try {
        yield put(TemplateLoadStart(templateName, isPublic, true));
        yield put(CloseGallery());
        yield put(SetIsTemplateOpen(true));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* previewTemplateFromGallery(action: TemplatePreviewFromGalleryAction): any {
    const { payload } = action;
    try {
        const templateContent = yield call(
            previewTemplate,
            payload.templateName,
            payload.isPublic,
            payload?.templateType
        );
        if (!templateContent) {
            return;
        }
        yield put(SetTemplatePreview(templateContent, payload.templateName, payload.isPublic));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* createContactList({ payload }: AddContactListStartAction): any {
    const { blockClientId, list } = payload;
    try {
        yield put(UpdateDialog({ isLoading: true }));
        const addedListResponse = yield call(addList, list);
        const listId = addedListResponse?.data?.PublicListID;
        yield put(ListsGetNamesStart());

        const { template } = yield select(getLandingPageCreatorStore);
        const matchedTemplateForm: EditorTemplateForm = template.forms?.find(
            (templateForm) => templateForm.blockClientId === blockClientId
        );
        if (matchedTemplateForm) {
            yield put(
                TemplateFormPayloadUpdate(blockClientId, {
                    Audience: [...(matchedTemplateForm?.formPayload?.Audience ?? []), listId],
                })
            );
        }
        yield put(HideDialog());
    } catch (e) {
        Logger.error(e?.response?.data?.Error ?? e);
        yield put(UpdateDialog({ isLoading: false }));
        yield put(AddContactListErrorStatus(true));
    }
}

function* loadPageTemplateSaga({ payload }: LoadPageTemplateStartAction): any {
    const { pageTemplateType } = payload;
    try {
        const pageTemplate = yield call(formsPagePreview, pageTemplateType);
        yield put(LoadPageTemplateSuccess(pageTemplateType, pageTemplate?.data));
    } catch (e) {
        yield call(errorToast, e);
    }
}

function* showSaveErrors(): any {
    const { template }: LandingPageCreatorState = yield select(getLandingPageCreatorStore);
    const blocksValidationErrors = template.forms?.filter((form) => !form.isDeleted && form.isError);
    if (!blocksValidationErrors.length) {
        return;
    }
    yield put(ShowTemplateErrorToast(i18n.t('fixerrorstocontinue'), 336));
}

function* watchLoadStart(): any {
    yield takeEvery(LandingPageCreatorActionTypes.LOAD_START, load);
    yield takeEvery(LandingPageCreatorActionTypes.SAVE_START, save);
    // yield takeLeading(LandingPageCreatorActionTypes.TEMPLATE_LOAD_SUCCESS, saveFormAfterTemplateisLoaded); // TODO: for now we won't be saving forms after loading templates.
    yield takeEvery(LandingPageCreatorActionTypes.TEMPLATE_LOAD_START, loadTemplate);
    // yield takeEvery(LandingPageCreatorActionTypes.TEMPLATE_SAVE_START, saveTemplate);
    yield takeLatest(LandingPageCreatorActionTypes.CLOSE, onClose);
    yield takeEvery(LandingPageCreatorActionTypes.TEMPLATE_LOAD_SINGLE_START, loadTemplateSingle);
    yield takeLatest(LandingPageCreatorActionTypes.TEMPLATE_OPEN_FROM_GALLERY, openTemplateFromGallery);
    yield takeLatest(LandingPageCreatorActionTypes.TEMPLATE_PREVIEW_FROM_GALLERY, previewTemplateFromGallery);
    yield takeLatest(LandingPageCreatorActionTypes.TEMPLATE_FORMS_CHECK_INVALID, checkTemplateFormsByAction);
    yield takeLatest(LandingPageCreatorActionTypes.ADD_CONTACT_LIST_START, createContactList);
    yield takeEvery(LandingPageCreatorActionTypes.LOAD_PAGE_TEMPLATE_START, loadPageTemplateSaga);
    yield takeLatest(LandingPageCreatorActionTypes.TEMPLATE_SHOW_SAVE_ERRORS, showSaveErrors);
}

const landingPageCreatorSagas = [fork(watchLoadStart)];

export default landingPageCreatorSagas;
